/**
 * SWISS School Portal - Dashboard ("Today")
 * ==========================================
 * Reads everything from the admin store via adminApi so the school
 * sees the same data the MSS admin sees.
 *
 * Redesigned per the unified spec (turn 2) as a priority queue
 * rather than a status wall: what needs you today, up top, with one
 * clear next action. The full phase tracker moved to Certification
 * Progress (/progress, turn 3) — the design is explicit that the
 * home screen stays uncluttered and sections own their own detail.
 * Nothing was dropped: tier deliverables are still here, folded into
 * a disclosure below the fold.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import {
  tierLabel, tierBlurb, normalizeTierKey, schoolHasModule,
} from '../services/tiers';
import {
  listTeachers,
  listCouncilMembers,
  getSchoolPhases,
  listIncidents,
  getAnonOverdue,
} from '../services/adminApi';
import { CERTIFICATION_PHASES, COUNCIL_REQUIRED_ROLES } from '../services/adminMockData';
import {
  GraduationCap,
  Users,
  Calendar,
  CheckCircle,
  Clock,
  Mail,
  AlertCircle,
  ShieldAlert,
  Target,
  ChevronDown,
} from 'lucide-react';
import { Skeleton } from '../components/Skeleton';
import AnonOverdueBanner from '../components/AnonOverdueBanner';
import { SUPPORT_EMAIL } from '../config/navigation';
import './Dashboard.css';

// What each programme tier actually includes — used by the
// "Your programme includes" disclosure so a school sees the right
// deliverables for what it has paid for, rather than the generic
// SWISS certification flow.
const TIER_DELIVERABLES = {
  bridge: [
    'CPD-accredited counsellor training',
    'Monthly clinical supervision',
    'Validated screening (PHQ-A · GAD-7 · ASQ) — 3× a year',
    'Risk dashboard with verifiable audit trail',
    'Quarterly principal reports',
  ],
  lite: [
    'Monthly visiting MSS counsellor',
    'Up to 10 wellness leads trained',
    'Policies toolkit (templates)',
    'Anonymous reporting',
    'Email crisis advisory',
  ],
  essentials: [
    'Gatekeeper certification — 4 hrs, CPD-accredited',
    'Quarterly Teacher Labs — 2 hrs × 4 (8 hrs / year)',
    'Policies toolkit — psychological safety, grievance, harassment',
    'Wellness Council — 5-member team, annual simulation',
    'Crisis response — rapid response, 24/7 recovery',
    'SWISS Platform access + Anonymous Reporting',
    'CPD UK accredited',
  ],
  compliance: [
    'Everything in SWISS Essentials',
    'POSH compliance — IC composition, External Member, annual return',
    'POCSO compliance — Child Protection Committee, mandatory-reporting protocol',
    'Student awareness modules across 3 age tracks',
    'Compliance dashboard',
  ],
  complete: [
    'Everything in SWISS Compliance',
    'Annual physical-safety audit — fire, electrical, transport, kitchen, playground, medical',
    'Pre-audit gap assessment + post-audit remediation tracker',
    'NCPCR-aligned documentation pack',
    'CBSE inspection readiness file',
  ],
};

function greeting(date = new Date()) {
  const h = date.getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

const Dashboard = () => {
  const { school } = useAuth();
  const schoolId = school?.id;
  const [deliverablesOpen, setDeliverablesOpen] = useState(false);

  const teachersQ = useQuery({
    queryKey: ['school', schoolId, 'teachers'],
    queryFn: () => listTeachers(schoolId),
    enabled: !!schoolId,
    staleTime: 30 * 1000,
  });
  const councilQ = useQuery({
    queryKey: ['school', schoolId, 'council'],
    queryFn: () => listCouncilMembers(schoolId),
    enabled: !!schoolId,
    staleTime: 30 * 1000,
  });
  const phasesQ = useQuery({
    queryKey: ['school', schoolId, 'phases'],
    queryFn: () => getSchoolPhases(schoolId),
    enabled: !!schoolId,
    staleTime: 30 * 1000,
  });
  const incidentsQ = useQuery({
    queryKey: ['school', schoolId, 'incidents'],
    queryFn: () => listIncidents({ schoolId }),
    enabled: !!schoolId,
    staleTime: 30 * 1000,
  });
  // Role-scoped — for a school user this returns this school's
  // anonymous-report summary { received, open, closed, overdue }.
  // Anonymous reporting is gated by tier / add-ons. On-Demand schools
  // (base = Dashboard + Resources) don't see the overdue banner or
  // the per-school anon summary unless 'anon' is in addOnModules.
  const showAnon = schoolHasModule(school, 'anon');
  const anonQ = useQuery({
    queryKey: ['anon', 'overdue'],
    queryFn: getAnonOverdue,
    staleTime: 60 * 1000,
    retry: 1,
    enabled: showAnon,
  });

  const teachers = teachersQ.data || [];
  const council = councilQ.data || [];
  const phases = phasesQ.data || {};
  const incidents = incidentsQ.data || [];
  const isLoading = teachersQ.isLoading || phasesQ.isLoading;

  // Teacher stats
  const teacherStats = (() => {
    const total = teachers.length;
    const certified = teachers.filter((t) => t.certifiedAt).length;
    const percentage = total ? Math.round((certified / total) * 100) : 0;
    return { total, certified, percentage };
  })();

  // Council required-role coverage. The required-roles set is the
  // single source of truth in adminMockData.js — keep it in lockstep
  // with the council page so the two never disagree on "x of y filled".
  const REQUIRED_ROLES = COUNCIL_REQUIRED_ROLES;
  const filledRoles = new Set(council.map((m) => m.role));
  const requiredFilled = REQUIRED_ROLES.filter((r) => filledRoles.has(r)).length;

  // Phase progress
  const completedPhases = CERTIFICATION_PHASES.filter(
    (p) => phases[p.key]?.status === 'completed',
  ).length;
  const currentPhaseIdx = (() => {
    const idx = CERTIFICATION_PHASES.findIndex((p) => phases[p.key]?.status !== 'completed');
    return idx === -1 ? CERTIFICATION_PHASES.length - 1 : idx;
  })();
  const currentPhase = Math.min(currentPhaseIdx + 1, CERTIFICATION_PHASES.length);
  const currentPhaseDef = CERTIFICATION_PHASES[currentPhaseIdx];
  const phaseLabel = currentPhaseDef?.label || '';

  // Next scheduled phase (if any)
  const upcomingPhase = CERTIFICATION_PHASES.find((p) => {
    const ph = phases[p.key];
    return ph?.status === 'scheduled' && ph?.scheduledAt;
  });
  const upcomingPhaseDetail = upcomingPhase ? phases[upcomingPhase.key] : null;

  // Recent activity — derive from incident actions + phase completions
  const activity = (() => {
    const items = [];
    for (const inc of incidents.slice(0, 5)) {
      const last = inc.actions?.[inc.actions.length - 1];
      // The action objects coming back from /admin/incidents don't
      // reliably carry `notes` / `timestamp` — this rendered as
      // "<title> · undefined · Invalid Date" on the dashboard. That
      // predates the redesign (the same expression is on main), but
      // it's user-visible, so fall back rather than print undefined.
      // The underlying shape mismatch still wants fixing at source.
      const note = last?.notes || last?.note || null;
      const stamp = last?.timestamp || last?.at || last?.createdAt || inc.reportedAt;
      items.push({
        id: last ? `inc_${inc.id}_${last.id ?? 'last'}` : `inc_${inc.id}_reported`,
        icon: AlertCircle,
        tone: 'warn',
        message: note ? `${inc.title} · ${note}` : `Incident reported: ${inc.title}`,
        timestamp: stamp,
      });
    }
    for (const ph of CERTIFICATION_PHASES) {
      const detail = phases[ph.key];
      if (detail?.status === 'completed' && detail.completedAt) {
        items.push({
          id: `phase_${ph.key}`,
          icon: CheckCircle,
          tone: 'brand',
          message: `Phase complete: ${ph.label}`,
          timestamp: detail.completedAt,
        });
      }
    }
    return items
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 5);
  })();

  const tierKey = normalizeTierKey(school?.tier);
  const tierName = tierLabel(school?.tier);
  const tierBlurbText = tierBlurb(school?.tier);
  const showCertFlow = schoolHasModule(school, 'progress');
  const showTeachersCard = schoolHasModule(school, 'teachers');
  const showCouncilCard = schoolHasModule(school, 'council');
  const deliverables = TIER_DELIVERABLES[tierKey] || [];

  const anon = anonQ.data || {};
  const schoolName = school?.shortName || school?.name || 'Your School';

  const fmtDate = (iso, opts) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;   // never render "Invalid Date"
    return d.toLocaleDateString('en-IN', opts || { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="today">
      <div className="today-head">
        <h1 className="today-greeting">{greeting()}, {schoolName}</h1>
        <p className="today-sub">
          {tierName}
          {tierBlurbText ? ` · ${tierBlurbText}` : ' · here’s what needs you today'}
        </p>
      </div>

      {showAnon && <AnonOverdueBanner linkTo="/anon-reports" />}

      {/* ── Next step ─────────────────────────────────────── */}
      {showCertFlow && currentPhaseDef && (
        <div className="today-next">
          <div className="today-next-mark" aria-hidden="true"><Target size={22} /></div>
          <div className="today-next-body">
            <div className="today-next-eyebrow">
              Next step · Phase {currentPhase} of {CERTIFICATION_PHASES.length} · {phaseLabel}
            </div>
            <h2 className="today-next-title">{currentPhaseDef.description || phaseLabel}</h2>
            <p className="today-next-detail">
              {completedPhases} of {CERTIFICATION_PHASES.length} phases complete
              {upcomingPhaseDetail?.scheduledAt
                ? ` · ${upcomingPhase.label} scheduled ${fmtDate(upcomingPhaseDetail.scheduledAt)}`
                : ''}
            </p>
          </div>
          <Link to="/progress" className="today-next-cta">
            View progress
          </Link>
        </div>
      )}

      {/* ── Stat tiles ────────────────────────────────────── */}
      <div className="today-stats">
        {showAnon && (
          <Link to="/anon-reports" className="today-stat">
            <div className="today-stat-icon marigold" aria-hidden="true"><ShieldAlert size={17} /></div>
            <div>
              <div className="today-stat-label">Anonymous reports</div>
              <div className="today-stat-value">
                {anon.open ?? 0} open
                {anon.overdue ? ` · ${anon.overdue} overdue` : ''}
              </div>
            </div>
          </Link>
        )}

        {showTeachersCard && (
          <Link to="/teachers" className="today-stat">
            <div className="today-stat-icon teal" aria-hidden="true"><GraduationCap size={17} /></div>
            <div>
              <div className="today-stat-label">Teachers certified</div>
              <div className="today-stat-value">
                {teacherStats.certified} of {teacherStats.total}
              </div>
            </div>
          </Link>
        )}

        {showCouncilCard && (
          <Link to="/council" className="today-stat">
            <div className="today-stat-icon clay" aria-hidden="true"><Users size={17} /></div>
            <div>
              <div className="today-stat-label">Wellness Council</div>
              <div className="today-stat-value">
                {requiredFilled} of {REQUIRED_ROLES.length} roles
              </div>
            </div>
          </Link>
        )}

        {showCertFlow && upcomingPhaseDetail?.scheduledAt && (
          <Link to="/progress" className="today-stat">
            <div className="today-stat-icon clay" aria-hidden="true"><Calendar size={17} /></div>
            <div>
              <div className="today-stat-label">Next {upcomingPhase.label}</div>
              <div className="today-stat-value">{fmtDate(upcomingPhaseDetail.scheduledAt)}</div>
            </div>
          </Link>
        )}
      </div>

      {/* ── Activity + coordinator ────────────────────────── */}
      <div className="today-split">
        <section className="today-panel">
          <h3 className="today-panel-head">
            <Clock size={16} aria-hidden="true" />
            Recent activity
          </h3>
          <div className="today-panel-body">
            {isLoading && (
              <div aria-busy="true">
                <Skeleton width="80%" height={12} />
                <div style={{ height: 8 }} />
                <Skeleton width="65%" height={12} />
                <div style={{ height: 8 }} />
                <Skeleton width="75%" height={12} />
              </div>
            )}
            {!isLoading && activity.length === 0 && (
              <p className="today-empty">
                No activity yet. Updates from your MSS Admin will appear here.
              </p>
            )}
            {activity.map((item) => {
              const Icon = item.icon || AlertCircle;
              return (
                <div key={item.id} className="today-activity">
                  <Icon size={15} className={`today-activity-icon ${item.tone}`} aria-hidden="true" />
                  <span className="today-activity-text">
                    {item.message}
                    {fmtDate(item.timestamp, { month: 'short', day: 'numeric' }) && (
                      <span className="today-activity-time">
                        {' · '}
                        {fmtDate(item.timestamp, { month: 'short', day: 'numeric' })}
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </section>

        <section className="today-panel">
          <h3 className="today-panel-head">Your SWISS coordinator</h3>
          <div className="today-panel-body today-support">
            <a className="today-support-link" href={`mailto:${SUPPORT_EMAIL}`}>
              <Mail size={15} aria-hidden="true" />
              {SUPPORT_EMAIL}
            </a>
            <p className="today-support-note">
              iCall helpline · 9152987821
            </p>
            <Link to="/crisis" className="today-support-cta">
              Open Crisis Dashboard
            </Link>
          </div>
        </section>
      </div>

      {/* ── Programme deliverables ────────────────────────── */}
      {deliverables.length > 0 && (
        <section className="today-programme">
          <button
            type="button"
            className="today-programme-toggle"
            onClick={() => setDeliverablesOpen((v) => !v)}
            aria-expanded={deliverablesOpen}
          >
            <span>Your {tierName} programme includes</span>
            <ChevronDown
              size={16}
              className={`today-chevron ${deliverablesOpen ? 'open' : ''}`}
              aria-hidden="true"
            />
          </button>
          {deliverablesOpen && (
            <ul className="today-programme-list">
              {deliverables.map((item) => (
                <li key={item}>
                  <CheckCircle size={14} aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
};

export default Dashboard;
